// JnJWorkersMode — full worker management for a J&J shift (Kenvue / Ceva).
// Replicates the Workers Register flow but scoped per shift and replaces
// "Permits" with four HR document categories:
//   1. Employment Contract (admin upload/replace)
//   2. Termination Notice (auto-set when supervisor marks Resign/Terminated)
//   3. Verbal Warnings (multiple entries; supervisor + admin)
//   4. Written Warnings — three slots (1st / 2nd / 3rd / final)
//
// Layout: a left-rail of workers (Ceva + Kenvue shifts grouped) and a right
// panel showing the selected worker's docs in tabs.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, FileWarning, Megaphone, FilePlus2, Trash2, Upload,
  ChevronRight, Search, AlertTriangle, CheckCircle2, RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { api } from "../api";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- helpers ----------
async function fileToB64(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || "").split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}

// ============================================================================
// Main component
// ============================================================================
export default function JnJWorkersMode({ isAdmin }) {
  const [shifts, setShifts] = useState([]);            // grouped { parent, shifts: [...] }
  const [activeShift, setActiveShift] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadShifts = useCallback(async () => {
    try {
      const { data } = await api.get("/jnj/companies");
      setShifts(data || []);
      // Auto-select the first shift on first load
      if (!activeShift) {
        const first = (data || [])[0]?.shifts?.[0];
        if (first) setActiveShift(first);
      }
    } catch {
      toast.error("Could not load shifts");
    }
  }, [activeShift]);

  const loadWorkers = useCallback(async () => {
    if (!activeShift) return;
    setBusy(true);
    try {
      const { data } = await api.get("/jnj/workers", {
        params: { company_id: activeShift.id, include_terminated: true },
      });
      setWorkers(data.workers || []);
    } catch {
      toast.error("Could not load workers");
    } finally {
      setBusy(false);
    }
  }, [activeShift]);

  useEffect(() => { loadShifts(); }, [loadShifts]);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workers;
    return workers.filter((w) =>
      (w.name || "").toLowerCase().includes(q) ||
      (w.code || "").toLowerCase().includes(q),
    );
  }, [workers, search]);

  const selected = filtered.find((w) => w.id === selectedWorkerId)
                || workers.find((w) => w.id === selectedWorkerId);

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left rail — shifts + worker list */}
      <aside className="col-span-12 md:col-span-4 lg:col-span-3 space-y-3">
        {/* Shift picker */}
        <div className="rounded-lg border hairline surface-1 p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Shift</div>
          <div className="space-y-1">
            {shifts.map((parent) => (
              <div key={parent.parent} className="text-sm">
                <div className="text-slate-400 text-xs font-semibold mb-1">{parent.parent}</div>
                {(parent.shifts || []).map((sh) => (
                  <button key={sh.id}
                    onClick={() => { setActiveShift(sh); setSelectedWorkerId(null); }}
                    data-testid={`jnj-workers-shift-${sh.id}`}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between gap-2 transition
                      ${activeShift?.id === sh.id
                        ? "bg-sky-500/15 text-sky-200 border border-sky-500/40"
                        : "text-slate-300 hover:bg-slate-800/40"}`}>
                    <span className="truncate">{sh.name.replace(parent.parent, "").replace("—", "").trim()}</span>
                    <span className="text-slate-600 num">{sh.workers}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Worker list */}
        <div className="rounded-lg border hairline surface-1">
          <div className="p-3 border-b hairline">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">
                Workers · {activeShift?.name || "—"}
              </div>
              <button onClick={loadWorkers} disabled={busy}
                className="text-slate-500 hover:text-slate-300"
                title="Refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or code"
                className="bg-[#0a0e1a] border-slate-800 pl-7 h-8 text-xs"
                data-testid="jnj-workers-search" />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2 space-y-1">
            {filtered.length === 0 && (
              <div className="text-center text-xs text-slate-500 py-6">No workers.</div>
            )}
            {filtered.map((w) => {
              const terminated = w.status === "terminated";
              return (
                <button key={w.id}
                  onClick={() => setSelectedWorkerId(w.id)}
                  data-testid={`jnj-worker-${w.id}`}
                  className={`w-full text-left px-2 py-2 rounded transition flex items-center gap-2
                    ${selectedWorkerId === w.id
                      ? "bg-sky-500/15 border border-sky-500/40"
                      : "hover:bg-slate-800/30 border border-transparent"}
                    ${terminated ? "opacity-60" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium truncate ${terminated ? "line-through text-slate-500" : "text-slate-200"}`}>
                      {w.name}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {w.has_contract && <Pill tone="emerald" testid={`pill-contract-${w.id}`}>C</Pill>}
                      {w.verbal_count > 0 && <Pill tone="amber" testid={`pill-verbal-${w.id}`}>V{w.verbal_count}</Pill>}
                      {w.written_count > 0 && (
                        <Pill tone={w.highest_written_slot === 3 ? "red" : "orange"} testid={`pill-written-${w.id}`}>
                          W{w.highest_written_slot}/3
                        </Pill>
                      )}
                      {w.has_termination && <Pill tone="red" testid={`pill-term-${w.id}`}>T</Pill>}
                      {!w.has_contract && !w.has_termination && w.verbal_count === 0 && w.written_count === 0 && (
                        <span className="text-[9px] text-slate-600">no docs</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Right panel — selected worker docs */}
      <main className="col-span-12 md:col-span-8 lg:col-span-9">
        {selected
          ? <WorkerDocsPanel key={selected.id} worker={selected} isAdmin={isAdmin} onRefresh={loadWorkers} />
          : <EmptyPanel />}
      </main>
    </div>
  );
}

function Pill({ tone, children, testid }) {
  const cls = {
    emerald: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    amber:   "bg-amber-500/20 text-amber-300 border-amber-500/40",
    orange:  "bg-orange-600/25 text-orange-200 border-orange-500/40",
    red:     "bg-red-600/25 text-red-200 border-red-500/40",
  }[tone];
  return (
    <span data-testid={testid}
      className={`text-[9px] num px-1.5 py-px rounded border ${cls}`}>
      {children}
    </span>
  );
}

function EmptyPanel() {
  return (
    <div className="rounded-lg border hairline surface-1 p-10 text-center min-h-[400px] flex flex-col items-center justify-center">
      <FileText className="w-12 h-12 text-slate-700 mb-3" />
      <div className="text-sm text-slate-400">Pick a worker on the left to manage their documents.</div>
      <div className="text-xs text-slate-600 mt-2">
        Employment contract · Verbal warnings · Written warnings · Termination notice
      </div>
    </div>
  );
}

// ============================================================================
// Worker docs panel — tabs for the 4 categories
// ============================================================================
const TABS = [
  { key: "contract",   label: "Employment Contract",  icon: FileText,    color: "emerald" },
  { key: "verbal",     label: "Verbal Warnings",      icon: Megaphone,   color: "amber" },
  { key: "written",    label: "Written Warnings",     icon: FileWarning, color: "orange" },
  { key: "termination",label: "Termination Notice",   icon: FilePlus2,   color: "red" },
];

function WorkerDocsPanel({ worker, isAdmin, onRefresh }) {
  const [docs, setDocs] = useState(null);
  const [tab, setTab] = useState("contract");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/jnj/workers/${worker.id}/docs`);
      setDocs(data);
    } catch {
      toast.error("Could not load worker docs");
    } finally {
      setBusy(false);
    }
  }, [worker.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border hairline surface-1" data-testid="worker-docs-panel">
      {/* Header */}
      <div className="px-5 py-4 border-b hairline flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Worker</div>
          <div className="text-lg font-semibold truncate flex items-center gap-2">
            <span className={worker.status === "terminated" ? "line-through text-slate-500" : ""}>
              {worker.name}
            </span>
            {worker.code && <span className="text-xs text-slate-500 num">{worker.code}</span>}
            {worker.status === "terminated" && (
              <span className="text-[10px] px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-200">
                {worker.terminated_kind === "resigned" ? "Resigned" : "Terminated"}
              </span>
            )}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
          className="h-8 border-slate-700">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="px-3 pt-3 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`worker-docs-tab-${t.key}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition flex items-center gap-1.5 whitespace-nowrap
                ${active
                  ? `border-${t.color}-500 text-${t.color}-200`
                  : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="p-5">
        {!docs ? (
          <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
        ) : tab === "contract" ? (
          <ContractTab worker={worker} doc={docs.employment_contract} isAdmin={isAdmin}
            onChanged={() => { load(); onRefresh && onRefresh(); }} />
        ) : tab === "verbal" ? (
          <VerbalTab worker={worker} list={docs.verbal_warnings || []} isAdmin={isAdmin}
            onChanged={() => { load(); onRefresh && onRefresh(); }} />
        ) : tab === "written" ? (
          <WrittenTab worker={worker} slots={docs.written_warnings || {}} isAdmin={isAdmin}
            onChanged={() => { load(); onRefresh && onRefresh(); }} />
        ) : (
          <TerminationTab worker={worker} doc={docs.termination_notice} />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Tab: Employment Contract
// ============================================================================
function ContractTab({ worker, doc, isAdmin, onChanged }) {
  const [uploadDlg, setUploadDlg] = useState(false);

  const onView = async () => {
    const res = await fetch(`${API}/api/jnj/workers/${worker.id}/employment-contract`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("mercy_token")}` } });
    if (!res.ok) { toast.error("Could not load"); return; }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this contract document?")) return;
    try {
      await api.delete(`/jnj/workers/${worker.id}/employment-contract`);
      toast.success("Contract removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      {doc ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
          <FileText className="w-8 h-8 text-emerald-300 flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-emerald-200 truncate">{doc.filename}</div>
            <div className="text-[11px] text-slate-400 mt-1 num">
              Uploaded {fmtDate(doc.uploaded_at)} by {doc.uploaded_by_name || "—"}
              {doc.size != null && <> · {Math.round((doc.size || 0) / 1024)} KB</>}
            </div>
            {doc.notes && <div className="text-xs text-slate-300 mt-2 italic">“{doc.notes}”</div>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={onView} className="h-8 bg-emerald-600 hover:bg-emerald-500"
                data-testid="contract-view">
                View
              </Button>
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setUploadDlg(true)}
                    className="h-8 border-slate-700" data-testid="contract-replace">
                    Replace
                  </Button>
                  <Button size="sm" variant="outline" onClick={onDelete}
                    className="h-8 border-red-700/50 text-red-300 hover:bg-red-500/10"
                    data-testid="contract-delete">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center">
          <FileText className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <div className="text-sm text-slate-400">No employment contract on file.</div>
          {isAdmin ? (
            <Button size="sm" onClick={() => setUploadDlg(true)}
              className="mt-3 bg-emerald-600 hover:bg-emerald-500"
              data-testid="contract-upload">
              <Upload className="w-3.5 h-3.5 mr-1" /> Upload contract
            </Button>
          ) : (
            <div className="text-[11px] text-slate-500 mt-2">Admin uploads required.</div>
          )}
        </div>
      )}

      <ContractUploadDialog open={uploadDlg} onClose={() => setUploadDlg(false)}
        workerId={worker.id} onSaved={() => { setUploadDlg(false); onChanged(); }} />
    </div>
  );
}

function ContractUploadDialog({ open, onClose, workerId, onSaved }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setNotes(""); } }, [open]);

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const b64 = await fileToB64(f);
    setFile({ filename: f.name, content_type: f.type || "application/pdf", data_base64: b64 });
  };

  const submit = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    setBusy(true);
    try {
      await api.post(`/jnj/workers/${workerId}/employment-contract`,
        { ...file, notes: notes.trim() });
      toast.success("Contract saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
        data-testid="contract-upload-dialog">
        <DialogHeader><DialogTitle>Upload employment contract</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Document (PDF or image)</Label>
            <input type="file" accept="application/pdf,image/*" onChange={onChange}
              className="block w-full mt-1 text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-emerald-600 file:text-emerald-50 file:px-2 file:py-1 file:text-xs hover:file:bg-emerald-500"
              data-testid="contract-upload-file" />
            {file && <div className="text-[11px] text-emerald-300 mt-1">✓ {file.filename}</div>}
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. 12-month renewable, starts 2026-06-01"
              className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="contract-upload-save">
            {busy ? "Saving..." : "Save contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Tab: Verbal Warnings (list)
// ============================================================================
function VerbalTab({ worker, list, isAdmin, onChanged }) {
  const [addDlg, setAddDlg] = useState(false);

  const onView = async (vid) => {
    const res = await fetch(
      `${API}/api/jnj/workers/${worker.id}/verbal-warning/${vid}/attachment`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("mercy_token")}` } });
    if (!res.ok) { toast.error("Could not load"); return; }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const onDelete = async (vid) => {
    if (!window.confirm("Delete this verbal warning entry?")) return;
    try {
      await api.delete(`/jnj/workers/${worker.id}/verbal-warning/${vid}`);
      toast.success("Removed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400">
          {list.length} verbal warning{list.length === 1 ? "" : "s"} on file
        </div>
        <Button size="sm" onClick={() => setAddDlg(true)}
          className="h-8 bg-amber-600 hover:bg-amber-500"
          data-testid="verbal-add">
          <Megaphone className="w-3.5 h-3.5 mr-1" /> Add verbal warning
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center text-sm text-slate-500">
          No verbal warnings recorded.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((v) => (
            <div key={v.id}
              data-testid={`verbal-row-${v.id}`}
              className="rounded-md border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-3">
              <Megaphone className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-slate-400 num">{fmtDate(v.date)}</div>
                <div className="text-sm text-amber-100 mt-0.5">{v.reason}</div>
                <div className="text-[10px] text-slate-500 mt-1">by {v.recorded_by_name || "—"}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {v.filename && (
                  <Button size="sm" variant="ghost" onClick={() => onView(v.id)}
                    className="h-7 px-2 text-xs text-amber-200 hover:bg-amber-500/10">
                    📎 {v.filename.length > 12 ? "View" : v.filename}
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="ghost" onClick={() => onDelete(v.id)}
                    className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <WarningAddDialog
        open={addDlg} onClose={() => setAddDlg(false)}
        kind="verbal" workerId={worker.id}
        onSaved={() => { setAddDlg(false); onChanged(); }}
      />
    </div>
  );
}

// ============================================================================
// Tab: Written Warnings (3 slots)
// ============================================================================
function WrittenTab({ worker, slots, isAdmin, onChanged }) {
  const [addDlg, setAddDlg] = useState(null);  // { slot }

  const onView = async (slot) => {
    const res = await fetch(
      `${API}/api/jnj/workers/${worker.id}/written-warning/${slot}/attachment`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("mercy_token")}` } });
    if (!res.ok) { toast.error("Could not load"); return; }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const onClear = async (slot) => {
    if (!window.confirm(`Clear written warning #${slot}? Cannot be undone.`)) return;
    try {
      await api.delete(`/jnj/workers/${worker.id}/written-warning/${slot}`);
      toast.success("Cleared");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="text-xs text-slate-400 mb-3 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />
        Three escalating slots — the 3rd is the <strong className="text-red-300">final</strong> warning.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {["1", "2", "3"].map((slot) => {
          const w = slots[slot];
          const isFinal = slot === "3";
          const tone = isFinal ? "red" : "orange";
          return (
            <div key={slot}
              data-testid={`written-slot-${slot}`}
              className={`rounded-lg border p-3 ${w
                ? (isFinal ? "border-red-500/40 bg-red-500/5" : "border-orange-500/30 bg-orange-500/5")
                : "border-dashed border-slate-700 bg-slate-900/30"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`text-[10px] uppercase tracking-widest font-semibold
                  ${w ? (isFinal ? "text-red-300" : "text-orange-300") : "text-slate-500"}`}>
                  {isFinal ? "FINAL WARNING" : `${slot}${slot === "1" ? "st" : "nd"} warning`}
                </div>
                {w && <CheckCircle2 className={`w-3.5 h-3.5 ${isFinal ? "text-red-400" : "text-orange-400"}`} />}
              </div>
              {w ? (
                <>
                  <div className="text-[11px] text-slate-400 num">{fmtDate(w.date)}</div>
                  <div className={`text-sm mt-1 ${isFinal ? "text-red-100" : "text-orange-100"}`}>{w.reason}</div>
                  <div className="text-[10px] text-slate-500 mt-1">by {w.recorded_by_name || "—"}</div>
                  <div className="mt-3 flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onView(slot)}
                      className={`h-7 px-2 text-xs ${isFinal ? "text-red-200 hover:bg-red-500/10" : "text-orange-200 hover:bg-orange-500/10"}`}
                      data-testid={`written-view-${slot}`}>
                      📎 View letter
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => onClear(slot)}
                        className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                        data-testid={`written-clear-${slot}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <div className="text-xs text-slate-600 mb-2">Slot empty</div>
                  <Button size="sm" onClick={() => setAddDlg({ slot })}
                    className={`h-7 text-xs ${tone === "red"
                      ? "bg-red-700 hover:bg-red-600"
                      : "bg-orange-600 hover:bg-orange-500"}`}
                    data-testid={`written-add-${slot}`}>
                    <FilePlus2 className="w-3 h-3 mr-1" />
                    Fill slot
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <WarningAddDialog
        open={!!addDlg} onClose={() => setAddDlg(null)}
        kind="written" slot={addDlg?.slot}
        workerId={worker.id}
        onSaved={() => { setAddDlg(null); onChanged(); }}
      />
    </div>
  );
}

// ============================================================================
// Tab: Termination Notice (read-only — set via supervisor mark Resign/Terminated)
// ============================================================================
function TerminationTab({ worker, doc }) {
  const onView = async () => {
    const res = await fetch(
      `${API}/api/jnj/supervisor-termination-letter/${worker.id}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("mercy_token")}` } });
    if (!res.ok) { toast.error("Could not load"); return; }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (!doc) {
    return (
      <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center">
        <FilePlus2 className="w-10 h-10 text-slate-700 mx-auto mb-2" />
        <div className="text-sm text-slate-400">No termination notice.</div>
        <div className="text-[11px] text-slate-600 mt-2 max-w-sm mx-auto">
          A termination notice is generated automatically when a supervisor or admin
          marks this worker as <strong>Resign</strong> or <strong>Terminated</strong> in
          the Supervisor Manual tab.
        </div>
      </div>
    );
  }
  const isResign = doc.kind === "resigned";
  return (
    <div className={`rounded-lg border p-4 flex items-start gap-3
      ${isResign ? "border-orange-500/30 bg-orange-500/5" : "border-red-500/40 bg-red-500/5"}`}>
      <FilePlus2 className={`w-8 h-8 flex-shrink-0 mt-1 ${isResign ? "text-orange-300" : "text-red-300"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`text-sm font-medium ${isResign ? "text-orange-200" : "text-red-200"}`}>
            {isResign ? "Resignation letter" : "Termination notice"}
          </div>
          {doc.effective_date && (
            <span className="text-[10px] text-slate-500 num">effective {doc.effective_date}</span>
          )}
        </div>
        <div className="text-xs text-slate-400 mt-1 truncate">{doc.filename}</div>
        {doc.reason && <div className="text-sm text-slate-300 mt-2 italic">“{doc.reason}”</div>}
        <div className="text-[10px] text-slate-500 mt-1">
          by {doc.uploaded_by_name || "—"} on {fmtDate(doc.uploaded_at)}
        </div>
        <Button size="sm" onClick={onView}
          className={`h-8 mt-3 ${isResign ? "bg-orange-600 hover:bg-orange-500" : "bg-red-700 hover:bg-red-600"}`}
          data-testid="termination-view">
          View letter
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Shared add-warning dialog (verbal OR written)
// ============================================================================
function WarningAddDialog({ open, onClose, kind, slot, workerId, onSaved }) {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(new Date().toISOString().slice(0, 10));
    setReason("");
    setFile(null);
  }, [open]);

  const isWritten = kind === "written";
  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const b64 = await fileToB64(f);
    setFile({ filename: f.name, content_type: f.type || "application/octet-stream", data_base64: b64 });
  };

  const submit = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    if (!reason.trim()) { toast.error("Write a short reason"); return; }
    if (isWritten && !file) { toast.error("Written warnings need a signed letter attachment"); return; }
    setBusy(true);
    try {
      const body = { date, reason: reason.trim() };
      if (file) body.attachment = file;
      if (isWritten) {
        await api.post(`/jnj/workers/${workerId}/written-warning/${slot}`, body);
      } else {
        await api.post(`/jnj/workers/${workerId}/verbal-warning`, body);
      }
      toast.success(isWritten ? `Slot ${slot} saved` : "Verbal warning added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
        data-testid={`warning-add-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>
            {isWritten ? `${slot === "3" ? "Final" : `Written #${slot}`} written warning` : "Verbal warning"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reason *</Label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="What was the issue? Be specific."
              className="w-full mt-1 bg-[#0a0e1a] border border-slate-800 rounded p-2 text-sm focus:outline-none focus:border-slate-600" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">
              {isWritten
                ? <>Signed letter <span className="text-red-400">*</span></>
                : <>Attachment <span className="text-slate-600">(optional)</span></>}
            </Label>
            <input type="file" accept="image/*,application/pdf" onChange={onChange}
              className={`block w-full mt-1 text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:px-2 file:py-1 file:text-xs ${isWritten
                ? (slot === "3"
                    ? "file:bg-red-700 hover:file:bg-red-600 file:text-red-50"
                    : "file:bg-orange-600 hover:file:bg-orange-500 file:text-orange-50")
                : "file:bg-amber-600 hover:file:bg-amber-500 file:text-amber-50"}`} />
            {file && <div className="text-[11px] text-emerald-300 mt-1">✓ {file.filename}</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={isWritten
              ? (slot === "3" ? "bg-red-700 hover:bg-red-600" : "bg-orange-600 hover:bg-orange-500")
              : "bg-amber-600 hover:bg-amber-500"}>
            {busy ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
